import { useId, useState } from 'react';

import type { SavedFilter } from '../../utils/filterSettings';
import { isValidSavedFilterName } from '../../utils/savedFilters';
import { Button, IconButton } from '../Button';
import { Dialog, DialogContent, DialogDescription, DialogFooter } from '../Dialog';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Text } from '../Text';

export type SavedFiltersProps = {
  /** User-saved filter sets. Each behaves like a built-in shortcut when applied. */
  savedFilters: SavedFilter[];
  /** Apply a saved filter (loads it into the form, same as a built-in shortcut). */
  onApply: (saved: SavedFilter) => void;
  /** Remove a saved filter by id. */
  onRemove: (id: string) => void;
};

/**
 * The "Saved filters" section of the AdvancedFilterDialog: lists the user's saved filter
 * sets (apply / remove). Renders nothing when there are no saved filters — the control to
 * save a new one lives in the dialog's action row ({@link SaveFilterButton}).
 */
function SavedFilters({ savedFilters, onApply, onRemove }: SavedFiltersProps) {
  // Saved filter pending removal — drives the confirmation dialog.
  const [pendingRemoval, setPendingRemoval] = useState<SavedFilter | null>(null);

  const handleConfirmRemoval = () => {
    if (pendingRemoval) {
      onRemove(pendingRemoval.id);
      setPendingRemoval(null);
    }
  };

  if (savedFilters.length === 0) {
    return null;
  }

  return (
    <div className="border-grey-20 -mt-4 flex flex-col gap-2 border-b pb-3">
      <Text className="text-2xs text-grey-60 font-semibold uppercase">Saved filters</Text>
      <div className="grid grid-cols-3 gap-2">
        {savedFilters.map((saved) => (
          <div key={saved.id} className="relative">
            <Button
              type="button"
              variant="secondary"
              size="small"
              className="w-full justify-center truncate px-7 py-1 text-xs"
              onClick={() => onApply(saved)}
              data-testid={`saved-filters-apply-${saved.id}`}
            >
              <span className="truncate">{saved.label}</span>
            </Button>
            <IconButton
              icon="crossCircled"
              variant="hollow"
              size="icon"
              iconSize={12}
              onClick={(e) => {
                e.stopPropagation();
                setPendingRemoval(saved);
              }}
              aria-label={`Remove saved filter ${saved.label}`}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full text-grey-50 hover:bg-transparent hover:opacity-70"
              data-testid={`saved-filters-remove-${saved.id}`}
            />
          </div>
        ))}
      </div>

      {/* Nested inside AdvancedFilterDialog, so stack above it with z-60. */}
      <Dialog open={!!pendingRemoval} onOpenChange={(open) => !open && setPendingRemoval(null)}>
        <DialogContent
          className="z-60 w-full max-w-md gap-[18px] p-4"
          overlayClassName="z-60"
          title="Remove saved filter?"
          data-testid="saved-filters-remove-dialog"
          headerProps={{ className: 'p-0' }}
          titleProps={{ className: 'text-primary p-0 text-[18px] font-semibold' }}
        >
          <DialogDescription className="text-grey-80 p-0 text-xs font-normal leading-[1.4]">
            Are you sure you want to remove the saved filter
            {pendingRemoval ? ` "${pendingRemoval.label}"` : ''}? This action cannot be undone.
          </DialogDescription>
          <DialogFooter className="flex items-center justify-end gap-2 p-0">
            <Button
              variant="secondary"
              size="standard"
              onClick={() => setPendingRemoval(null)}
              data-testid="saved-filters-remove-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="standard"
              onClick={handleConfirmRemoval}
              data-testid="saved-filters-remove-confirm"
            >
              <Icon icon="trash" size={14} />
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export type SaveFilterButtonProps = {
  /** Existing saved filters, used to validate the new name against duplicates. */
  savedFilters: SavedFilter[];
  /** Save the current filter set under a name. */
  onSave: (name: string) => void;
};

/**
 * The "Save current filters" control for the AdvancedFilterDialog action row. Opens a
 * popover to name and save the current filter set; validates non-empty, unique names.
 */
function SaveFilterButton({ savedFilters, onSave }: SaveFilterButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const nameInputId = useId();

  const validation = isValidSavedFilterName(name, savedFilters);

  const handleSave = () => {
    if (!validation.ok) {
      return;
    }
    onSave(name.trim());
    setName('');
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setName('');
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="hollow"
          size="small"
          className="h-6 gap-1 px-px py-0 text-xs font-medium text-blue-50"
          data-testid="saved-filters-save-trigger"
        >
          <Icon icon="bookmark" size={14} />
          <Text className="text-xs text-blue-50">Save current filters</Text>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="flex w-64 flex-col gap-2 p-3">
        <Text asChild className="text-2xs text-grey-60 font-semibold uppercase">
          <label htmlFor={nameInputId}>Name this filter</label>
        </Text>
        <Input
          id={nameInputId}
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            }
          }}
          placeholder="e.g. My open requests"
          data-testid="saved-filters-name-input"
        />
        {!validation.ok && validation.reason === 'duplicate' && (
          <Text className="text-2xs text-red-50">A saved filter with this name already exists.</Text>
        )}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => handleOpenChange(false)}
            data-testid="saved-filters-save-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="small"
            disabled={!validation.ok}
            onClick={handleSave}
            data-testid="saved-filters-save-confirm"
          >
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { SavedFilters, SaveFilterButton };
